import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";

import {
	initialValues,
	validationSchema,
	t2InitialValues,
} from "./coastalWetlands";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import {
	useContextAwareGetCoastalWetlands,
	useContextAwareGetCoastalWetlandsDefaults,
	useUpdateCoastalWetlandsMutation,
} from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import type { ModuleFull } from "./coastalWetlandsTypes";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import useCoastalWetlandsSchema from "./useCoastalWetlandsSchema";
import { selectCurrentBuilder } from "@features/builder/builderSlice";

const CoastalWetlands = () => {
	// const newNotes = useAppSelector(selectNewCurrentModuleNotes);
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);

	const id = moduleList.find(({ id }) => id === FEModules.CoastalWetlands)?.uniqueId;

	const {
		data: coastalData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetCoastalWetlands(id ?? 0, { refetchOnMountOrArgChange: true });
	const [
		updateCoastalWetlands,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateCoastalWetlandsMutation();
	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetCoastalWetlandsDefaults(id ?? 0, { skip: !id },);
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });


	const { moduleSchema, t2Schema } = useCoastalWetlandsSchema();
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});
	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (coastalData) {
			setInitValues({
				// Start revegetation is locked at 0; this also resets old non-zero values on the next save
				module: { ...coastalData.module, area_w_restored_vegetation_start: 0 },
				tiertwo: { ...coastalData.tiertwo, ...defaults },
			});
			processModuleThreads({
				module: { ...coastalData.module },
			})
		}
	}, [coastalData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		try {
			const module = { ...values.module };
			if (values.module.id)
				await updateCoastalWetlands({
					data: { ...values, module },
					activityId: activeActivityId ?? 0,
				}).unwrap();
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			initialValues={initValues}
			onSubmit={handleSubmit}
			validationSchema={validationSchema}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.coastal_wetlands"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					isLoading={isLoadingUpdate}
					beModuleType={BEModulesClassNames.CoastalWetlands}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				/>
				<GenericTierTwo
					title="project.coastal_wetlands"
					t2Schema={t2Schema}
					warningMessage={defaultsErrorMsg ?? null}
				/>
			</>
		</Formik>
	);
};

export default CoastalWetlands;
